/**
 * Picking the handful of people an announcement is addressed to.
 *
 * The form used to take one nickname typed into a box. It takes a list now,
 * and the list is built the way the banlist builds one: the names are picked
 * from the accounts the portal knows, because a nickname typed by hand is a
 * message which reaches nobody and says it went out.
 *
 * Every name picked leaves the list of choices and gains a chip which can be
 * pressed to put it back. What travels with the form is one hidden field per
 * chip, so the endpoint reads a list whether it was built here or typed into
 * a request by somebody else.
 *
 * Without this script the picker is a plain select which carries its own
 * name, and one announcement reaches one player - exactly what the form did
 * before there was a list at all.
 */

import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** Which audience the names are for; the rest reach people by their role. */
const NAMED = "PLAYERS";

/**
 * Return the chip standing for `nickname`, ready to be put in the list.
 *
 * With the face the option of the picker carries, the same one the server
 * draws beside a name it puts on the list itself - so a chip added by hand
 * and a chip the page arrived with look the same.
 */
export function chipFor(nickname: string, drop: string, face = ""): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "recipient";
    chip.dataset["recipient"] = nickname;

    const carried = document.createElement("input");
    carried.type = "hidden";
    carried.name = "recipients";
    carried.value = nickname;

    const picture = document.createElement("img");
    picture.className = "avatar";
    picture.src = face || `/skins/head/${encodeURIComponent(nickname)}.png?s=20`;
    picture.width = 20;
    picture.height = 20;
    picture.alt = "";

    const label = document.createElement("span");
    label.textContent = nickname;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "recipient__drop";
    button.dataset["recipientDrop"] = "";
    button.setAttribute("aria-label", drop.replace("{username}", nickname));
    button.innerHTML =
        '<svg class="icon" width="12" height="12" aria-hidden="true">' +
        '<use href="/static/assets/icons/icons.svg#close"></use></svg>';

    chip.append(carried, picture, label, button);

    return chip;
}

/** Wire the picker of one form. */
export function bindRecipients(form: HTMLElement): void {
    const audience = query<HTMLSelectElement>("[data-audience-select]", form);
    const source = query<HTMLSelectElement>("[data-recipient-source]", form);
    const list = query<HTMLElement>("[data-recipient-list]", form);
    const field = query<HTMLElement>("[data-recipient-field]", form);

    if (!audience || !source || !list || !field) {
        return;
    }

    // The picker stops answering for itself the moment the list can hold
    // more than one name; from here on the chips are what is sent.
    source.removeAttribute("name");

    /**
     * Say out loud that the form now holds something else.
     *
     * A chip is a hidden field appearing or disappearing, which fires no
     * event of its own - so the guard which keeps the save button in step
     * with the form never heard about it.
     */
    const announce = (): void => {
        list.dispatchEvent(new Event("change", { bubbles: true }));
    };

    /** Return whether `nickname` is already in the list. */
    const holds = (nickname: string): boolean =>
        queryAll<HTMLElement>("[data-recipient]", list).some((chip) => chip.dataset["recipient"] === nickname);

    /** Put an option back among the choices, or take it away. */
    const offer = (nickname: string, available: boolean): void => {
        for (const option of Array.from(source.options)) {
            if (option.value === nickname) {
                option.hidden = !available;
                option.disabled = !available;
            }
        }
    };

    const add = (nickname: string): void => {
        if (!nickname || holds(nickname)) {
            return;
        }

        const face = Array.from(source.options).find((option) => option.value === nickname)?.dataset["face"] ?? "";
        list.append(chipFor(nickname, text("js.recipients.drop", "Odobrať {username}"), face));
        offer(nickname, false);
        announce();
    };

    /** Grey the picker out while the message is not going to named people. */
    const follow = (): void => {
        const named = audience.value.toUpperCase() === NAMED;

        field.classList.toggle("is-inactive", !named);
        source.disabled = !named;

        // Disabled fields are not submitted, so an announcement switched over
        // to everybody cannot quietly carry the names picked a minute ago.
        for (const carried of queryAll<HTMLInputElement>("input[name=recipients]", list)) {
            carried.disabled = !named;
        }

        for (const button of queryAll<HTMLButtonElement>("[data-recipient-drop]", list)) {
            button.disabled = !named;
        }
    };

    for (const chip of queryAll<HTMLElement>("[data-recipient]", list)) {
        offer(chip.dataset["recipient"] ?? "", false);
    }

    // The list drawn over the control follows the value it holds, and only
    // if it is told the value moved - which is this same event, so the
    // handler has to know when it is hearing itself.
    let quiet = false;

    source.addEventListener("change", () => {
        if (quiet) {
            return;
        }

        add(source.value);

        // Back to the prompt, so the same name is never the answer twice and
        // the closed control keeps saying what the field is for.
        source.selectedIndex = 0;
        quiet = true;
        source.dispatchEvent(new Event("change", { bubbles: true }));
        quiet = false;
    });

    list.addEventListener("click", (event: MouseEvent) => {
        const button = (event.target as HTMLElement | null)?.closest("[data-recipient-drop]");
        const chip = button?.closest<HTMLElement>("[data-recipient]");

        if (!chip) {
            return;
        }

        offer(chip.dataset["recipient"] ?? "", true);
        chip.remove();
        announce();
    });

    audience.addEventListener("change", follow);
    follow();
}

/** Wire every announcement form of the page. */
export function initRecipients(): void {
    for (const form of queryAll<HTMLElement>("[data-recipients]")) {
        bindRecipients(form);
    }
}
